import { getLatestValueFromHistory } from "../api/api";

const todayISO = () => new Date().toISOString().split("T")[0];

export const normalizeEffectiveDate = (date) => {
  if (!date) return "";
  return String(date).split("T")[0].substring(0, 10);
};

export const effectiveDateOrToday = (date) =>
  normalizeEffectiveDate(date) || todayISO();

/** null/undefined — не уволен */
export const normalizeIsFired = (isFired) => {
  if (isFired === undefined || isFired === null) return false;
  return isFired === true || isFired === "true" || isFired === 1;
};

/** ID отделений (department) из позиций или данных пользователя */
export const resolveDepartmentIds = (positions, userData) => {
  const fromPositions = (positions || [])
    .filter((p) => p.department?.value != null)
    .map((p) => Number(p.department.value));
  if (fromPositions.length > 0) {
    return [...new Set(fromPositions)];
  }
  if (userData?.department?.length) {
    return userData.department.map((d) => Number(d.id));
  }
  if (userData?.hr_data?.primary_department?.id != null) {
    return [Number(userData.hr_data.primary_department.id)];
  }
  if (userData?.departments?.length) {
    return userData.departments.map((d) => Number(d.id));
  }
  return [];
};

const resolveCompanyId = (formData, positions, userData) => {
  if (formData?.companyId != null && formData.companyId !== "") {
    return Number(formData.companyId);
  }
  const fromPosition = positions?.[0]?.company?.value;
  if (fromPosition != null) return Number(fromPosition);
  if (userData?.company_id != null) return Number(userData.company_id);
  return null;
};

const resolveCompanyEffectiveDate = (formData, positions, userData) => {
  const firstPosition = positions?.[0];
  return (
    formData?.companyIdEffectiveDate ||
    firstPosition?.companyEffectiveDate ||
    getLatestValueFromHistory(userData?.company_history, "company")
      ?.effective_date ||
    userData?.company_id_effective_date ||
    userData?.company_effective_date ||
    ""
  );
};

const resolveDepartmentEffectiveDate = (formData, positions, userData) => {
  const firstPosition = positions?.[0];
  return (
    firstPosition?.departmentEffectiveDate ||
    formData?.departmentEffectiveDate ||
    getLatestValueFromHistory(userData?.user_department_history, "department")
      ?.effective_date ||
    userData?.department_effective_date ||
    ""
  );
};

const resolveCompaniesArray = (companyId, userData) => {
  if (userData?.companies?.length) {
    return userData.companies.map((c) =>
      typeof c === "object" && c != null ? Number(c.id) : Number(c),
    );
  }
  return companyId != null ? [companyId] : [];
};

/**
 * Сборка тела PUT /auth/{user_id} по контракту бэкенда.
 */
export const buildUserUpdatePayload = ({
  formData,
  userData,
  positions = [],
  options = {},
}) => {
  const {
    includeOrg = true,
    includeSalary = true,
    includeWorkSchedule = true,
  } = options;

  const firstPosition = positions[0];
  const payload = {
    email: (formData.email || userData.email || "").trim(),
    surname: formData.lastName || userData.surname,
    name: formData.firstName || userData.name,
    patronymic: formData.patronymic ?? userData.patronymic ?? "",
    hire_date: normalizeEffectiveDate(formData.hireDate || userData.hire_date),
    birth_date: normalizeEffectiveDate(
      formData.birthDate || userData.birth_date,
    ),
    phone_number: formData.phone ?? userData.phone_number ?? "",
    hobby: (formData.hobbies ?? userData.hobby ?? "").trim() || "нет данных",
    access_levels:
      formData.accessLevel?.length > 0
        ? formData.accessLevel
        : (userData.access_levels || []).map((level) => level.id),
    access_level_effective_date: effectiveDateOrToday(
      formData.accessLevelEffectiveDate,
    ),
    is_fired:
      formData.isFired !== undefined
        ? formData.isFired
        : normalizeIsFired(userData.is_fired),
    is_fired_date: effectiveDateOrToday(
      formData.firedDate || userData.is_fired_date,
    ),
    grade: formData.grade ?? userData.hr_data?.grade ?? "",
    grade_effective_date: effectiveDateOrToday(
      formData.gradeEffectiveDate ||
        getLatestValueFromHistory(userData.user_grade_history, "grade")
          ?.effective_date,
    ),
  };

  if (includeSalary) {
    payload.salary =
      formData.salary !== "" && formData.salary != null
        ? Number(formData.salary)
        : userData.salary;
    payload.salary_effective_date = effectiveDateOrToday(
      formData.salaryEffectiveDate,
    );
  }

  if (includeWorkSchedule) {
    const workFormatDate = normalizeEffectiveDate(
      formData.workFormatEffectiveDate,
    );
    if (workFormatDate) {
      const workFormatId =
        formData.workFormat ?? userData.hr_data?.work_format?.id;
      if (workFormatId != null) {
        payload.work_format_id = workFormatId;
        payload.work_format_id_effective_date = workFormatDate;
      }
    }

    const workHoursDate = normalizeEffectiveDate(
      formData.workHoursPerDayEffectiveDate,
    );
    if (workHoursDate) {
      const workHours =
        formData.workHoursPerDay ?? userData.hr_data?.work_hours_per_day;
      if (workHours != null) {
        payload.work_hours_per_day = workHours;
        payload.work_hours_per_day_effective_date = workHoursDate;
      }
    }

    const workStartDate = normalizeEffectiveDate(
      formData.workStartTimeEffectiveDate,
    );
    if (workStartDate) {
      const workStartTime =
        formData.workStartTime || userData.hr_data?.work_start_time;
      if (workStartTime) {
        payload.work_start_time = workStartTime;
        payload.work_start_time_effective_date = workStartDate;
      }
    }
  }

  if (includeOrg) {
    const companyId = resolveCompanyId(formData, positions, userData);
    if (companyId != null) {
      const companyDate = effectiveDateOrToday(
        resolveCompanyEffectiveDate(formData, positions, userData),
      );
      payload.company_id = companyId;
      payload.company_id_effective_date = companyDate;
      payload.companies = resolveCompaniesArray(companyId, userData);
      payload.company_effective_date = companyDate;
    }

    const departmentIds = resolveDepartmentIds(positions, userData);
    if (departmentIds.length > 0) {
      payload.department = departmentIds;
      payload.department_effective_date = effectiveDateOrToday(
        resolveDepartmentEffectiveDate(formData, positions, userData),
      );
    }

    const currentGroups = positions
      .filter((p) => p.group?.value != null)
      .map((p) => Number(p.group.value));
    payload.group = currentGroups;
    payload.group_effective_date = effectiveDateOrToday(
      firstPosition?.groupEffectiveDate,
    );

    const currentRoles = positions
      .filter((p) => p.role?.value != null)
      .map((p) => Number(p.role.value));
    payload.role = currentRoles;
    payload.role_effective_date = effectiveDateOrToday(
      firstPosition?.roleEffectiveDate,
    );

    const currentSections = positions
      .filter((p) => p.section?.value != null)
      .map((p) => Number(p.section.value));
    payload.section = currentSections;
    payload.section_effective_date = effectiveDateOrToday(
      firstPosition?.sectionEffectiveDate,
    );

    const currentTeamLeaders = positions
      .filter((p) => p.teamLeader?.value != null)
      .map((p) => Number(p.teamLeader.value));
    payload.team_leaders = currentTeamLeaders;
    payload.team_leaders_effective_date = effectiveDateOrToday(
      firstPosition?.teamLeaderEffectiveDate,
    );

    payload.primary_group_id = firstPosition?.group?.value
      ? Number(firstPosition.group.value)
      : null;
    payload.primary_group_id_effective_date = effectiveDateOrToday(
      firstPosition?.groupEffectiveDate,
    );

    payload.primary_role_id = firstPosition?.role?.value
      ? Number(firstPosition.role.value)
      : null;
    payload.primary_role_id_effective_date = effectiveDateOrToday(
      firstPosition?.roleEffectiveDate,
    );

    payload.primary_section_id = firstPosition?.section?.value
      ? Number(firstPosition.section.value)
      : null;
    payload.primary_section_id_effective_date = effectiveDateOrToday(
      firstPosition?.sectionEffectiveDate,
    );

    payload.primary_team_leader_id = firstPosition?.teamLeader?.value
      ? Number(firstPosition.teamLeader.value)
      : null;
    payload.primary_team_leader_id_effective_date = effectiveDateOrToday(
      firstPosition?.teamLeaderEffectiveDate,
    );
  }

  return payload;
};

/** Удаляет пустые массивы и null/undefined перед отправкой */
export const cleanUserUpdatePayload = (payload) => {
  const cleaned = { ...payload };
  Object.keys(cleaned).forEach((key) => {
    if (Array.isArray(cleaned[key]) && cleaned[key].length === 0) {
      delete cleaned[key];
    } else if (cleaned[key] === null || cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });
  return cleaned;
};
